<template>
  <div id="classroom">
    <v-progress-circular
      v-if="loading"
      style="
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      "
      indeterminate
      color="primary"
      :size="62"
    >
    </v-progress-circular>
    <div v-else>
      <div v-if="hasVideo && loadingVideo" class="filler-div"></div>
      <div flat v-if="this.source.sources">
        <div class="video-player-div">
          <vue-plyr ref="plyrOffline" v-if="this.hasOfflineSupport">
            <video
              width="100%"
              height="auto"
              crossorigin="anonymous"
              controls
              :style="vuePlyrHeight"
              :src="this.videoResolution"
              @error="vuePlyrOnError()"
              @stalled="vuePlyrStalled()"
              @play="onVideoPlay"
            />
          </vue-plyr>

          <div
            v-else-if="videoSource === 'vimeo'"
            class="iframe-wrapper videoReady"
          >
            <video-player
              ref="vp"
              :media-source="this.source"
              :type="'classroom'"
              @timeupdate="onPlayerTimeupdate"
              @ended="onPlayerEnded"
              @canplay="onCanplay"
              @play="onVideoPlay"
              @enterpictureinpicture="onEnterpictureinpicture"
              :video-id="videoId"
            />
          </div>
          <div
            v-else
            :class="{ 'iframe-wrapper': true, videoReady: !this.loadingVideo }"
          >
            <iframe
              id="panda-XXXX"
              :src="`https://player-vz-6b763bac-40b.tv.pandavideo.com.br/embed/?v=${this.videoId}&autoplay=true&muted=false&playOpensFullscreen=true`"
              :style="`opacity:${loadingVideo === true ? '0' : '1'}`"
              class="iframe"
              allowfullscreen="true"
            ></iframe>
          </div>
        </div>
      </div>
      <div
        v-if="hasVideo"
        class="video-actions"
        :style="
          hasOfflineSupport || videoSource === 'vimeo' ? 'margin-top: 20px' : ''
        "
      >
        <v-btn
          :fab="width750()"
          :small="width750()"
          :disabled="loadingLesson"
          rounded
          color="primary"
          class="elevation-0"
          @click="moveLesson('previous')"
        >
          <v-icon dark>skip_previous</v-icon>
          {{ width750() ? null : "Aula anterior" }}
        </v-btn>
        <div
          style="
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 10px;
          "
        >
          <rating-display
            :disabled="loadingLesson"
            :model="this.rating"
            @onClick="openRatingDialog()"
          ></rating-display>
          <rating-dialog
            v-if="!this.isOffline"
            :title="this.sectionItem.learnable.lesson"
            :model="this.rating"
            :opened="this.showRatingDialog"
            @onCloseDialog="closeRatingDialog()"
            @onOpenDialog="openRatingDialog()"
          />

          <v-btn
            v-if="this.sectionItem.learnable.download"
            @click="saveLesson()"
            :loading="this.loadingOfflineSupport"
            :disabled="this.hasOfflineSupport || loadingLesson"
            :small="width450()"
            :text="width450()"
            :rounded="!width450()"
            :outlined="!width450()"
            color="primary"
          >
            <v-icon dense>download</v-icon>
            <span>
              {{ this.hasOfflineSupport ? "Salvo" : "Download" }}
            </span>
          </v-btn>
          <v-btn
            @click="getMock()"
            :text="width450()"
            :small="width450()"
            :rounded="!width450()"
            :outlined="!width450()"
            :disabled="loadingLesson"
            :loading="loadingMock"
            color="primary"
            v-if="
              this.sectionItem.learnable.subjects.data.length > 0 ||
              this.sectionItem.section_topic_id !== null
            "
          >
            <v-icon dense>assignment</v-icon>
            <span> Questões </span>
          </v-btn>
        </div>
        <v-btn
          :fab="width750()"
          :small="width750()"
          :disabled="loadingLesson"
          rounded
          color="primary"
          class="elevation-0"
          @click="moveLesson('next')"
        >
          {{ width750() ? null : "Próxima aula" }}
          <v-icon dark>skip_next</v-icon>
        </v-btn>
      </div>

      <v-skeleton-loader
        v-if="loadingLesson"
        type="heading"
        class="ml-5 mt-6"
      ></v-skeleton-loader>
      <h3 v-else class="class-title mt-5">
        <v-tooltip top v-if="this.hasOfflineSupport">
          <template v-slot:activator="{ on }">
            <v-icon
              style="margin-top: 2px; margin-right: 4px"
              color="green"
              v-on="on"
              >download</v-icon
            >
          </template>
          <span>Esta aula está disponível offline!</span>
        </v-tooltip>
        {{ this.sectionItem.learnable.lesson }}
      </h3>
      <v-flex xl12 lg12 sm12 v-if="this.sectionItem.learnable.body">
        <v-card flat v-if="this.sectionItem.learnable">
          <v-card-text>
            <div v-html="this.sectionItem.learnable.body"></div>
          </v-card-text>
        </v-card>
      </v-flex>
      <content-drawer
        class="app--drawer"
        @classChanged="classChanged()"
      ></content-drawer>

      <PodcastPlayer
        v-if="podcastAudioSrc"
        ref="podcastPlayer"
        :audioSrc="podcastAudioSrc"
        @podcast-play="pauseVideo"
      />

      <AITools
        :course-id="parseInt($route.params.courseId)"
        :chat-assistant="{}"
      />
    </div>
  </div>
</template>

<script>
import { CONFIG } from "@/config";
import VWidget from "@/components/VWidget";
import ResourceList from "~/components/widgets/list/ResourceList";
import CourseService from "@/services/courses.service";
import SessionAlertService from "@/services/sessionAlerts.service";
import RatingService from "@/services/ratings.service";
import VideoPlayer from "@/components/VideoPlayer";
import RatingDisplay from "../../components/class/RatingDisplay";
import RatingDialog from "../../components/class/RatingDialog";
import ContentDrawer from "../../components/ContentDrawer";
import TopicService from "../../services/topics.service";
import { set } from "vue";
import swService from "../../services/sw.service";
import DeviceDetector from "device-detector-js";
import AITools from "@/components/aiTools/AITools.vue";
import PodcastPlayer from "../../components/PodcastPlayer.vue";
const deviceDetector = new DeviceDetector();

export default {
  components: {
    PodcastPlayer,
    ContentDrawer,
    RatingDialog,
    RatingDisplay,
    VWidget,
    ResourceList,
    VideoPlayer,
    AITools,
  },
  data() {
    return {
      podcastAudioSrc: null,
      playerOptions: {
        title: "title",
        volume: 10,
        autoplay: true,
        muted: true,
        invertTime: true,
        speed: { selected: 1, options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] },
        controls: [
          "play-large", // The large play button in the center
          "restart", // Restart playback
          "rewind", // Rewind by the seek time (default 10 seconds)
          "play", // Play/pause playback
          "fast-forward", // Fast forward by the seek time (default 10 seconds)
          "progress", // The progress bar and scrubber for playback and buffering
          "current-time", // The current time of playback
          "duration", // The full duration of the media
          "mute", // Toggle mute
          "volume", // Volume control
          "captions", // Toggle captions
          "settings", // Settings menu
          "pip", // Picture-in-picture (currently Safari only)
          "airplay", // Airplay (currently Safari only)
          // 'download', // Show a download button with a link to either the current source or a custom URL you specify in your options
          "fullscreen", // Toggle fullscreen
        ],
      },
      sectionItem: {
        learnable: {
          lesson: null,
        },
      },
      rating: null,
      showRatingDialog: false,
      subjectsLabel: "",
      source: {},
      loadingMock: false,
      loading: true,
      loadingLesson: true,
      loadingVideo: true,
      hasVideo: true,
      videoBtns: [
        { title: "Avaliar", action: this.openRatingDialog },
        { title: "Baixar", action: this.saveLesson },
        { title: "Questões", action: this.getMock },
      ],
      videoResolution: "",
      videoId: null,
      videoSource: null,
      videoHeight: "calc(100vh - 56px - 132px)",
      loadingOfflineSupport: false,
      hasOfflineSupport:
        localStorage.getItem(
          "hasOfflineSupport_" + this.$route.params.sectionItemId,
        ) == "1",
      plyrOfflineElement: null,
      isFullscreen: false,
      sections: [],
      sections_items: {},
      offline_section_items: [],
    };
  },

  mounted() {
    CourseService.getSectionsByCourseId(this.$route.params.courseId)
      .then((response) => {
        this.sections = response.data;
        this.sections_items = this.sections.reduce((acc, section) => {
          for (const item of section.items.data) {
            if (item && item.id) {
              acc[item.id] = item;
            }
          }
          return acc;
        }, {});
        this.offline_section_items = Object.keys(localStorage)
          .filter((x) => x.startsWith("hasOfflineSupport_"))
          .map((x) => this.sections_items[x.replace("hasOfflineSupport_", "")])
          .filter((_) => _);
      })
      .catch((error) => {
        console.log("CourseService.getSectionsByCourseId", error);
      });
  },
  updated() {
    this.$nextTick(() => {
      try {
        if (this.$refs.plyrOffline.player) {
          this.plyrOfflineElement = this.$refs.plyrOffline.player;
          this.plyrOfflineElement.on("canplaythrough", (event) => {});
          this.plyrOfflineElement.on("error", (event) => {
            console.log("PlyrOfflineElement ERROR", event);
          });
          this.plyrOfflineElement.on("stalled", (event) => {
            console.log("PlyrOfflineElement stalled", event);
          });
          this.plyrOfflineElement.on("enterfullscreen", () => {
            this.isFullscreen = true;
          });

          this.plyrOfflineElement.on("exitfullscreen", () => {
            this.isFullscreen = false;
          });
        }
      } catch (error) {
        console.log("plyrOffline or its player not available.", error);
      }
    });
  },

  created() {
    if (
      this.$route.params.sectionItemId === "undefined" ||
      this.$route.params.sectionItemId === null ||
      this.$route.params.sectionItemId === ""
    ) {
      this.$router.push("/courses");
    }
    this.loadLesson();
    this.loadRating();
  },

  metaInfo() {
    return {
      title:
        this.sectionItem != null
          ? "LS - " + this.sectionItem.learnable.lesson
          : "LS - Aula",
      meta: [
        {
          vmid: "description",
          name: "description",
          content:
            this.sectionItem != null
              ? "Aula em video de " + this.sectionItem.learnable.lesson
              : "Aula do curso preparatório preparatório para certificações financeiras",
        },
      ],
    };
  },

  computed: {
    player() {
      return this.$refs.vp.$refs.plyr.player;
    },
    isOffline() {
      if (!navigator) return false;
      return !navigator.onLine;
    },
    vuePlyrHeight() {
      return {
        maxHeight: this.isFullscreen ? "100%" : "60vh",
      };
    },
  },
  methods: {
    loadPodcastAudio() {
      this.podcastAudioSrc = null;

      if (
        this.sectionItem &&
        this.sectionItem.learnable &&
        this.sectionItem.audio &&
        this.sectionItem.audio.url
      ) {
        this.podcastAudioSrc = this.sectionItem.audio.url;
      }
    },

    async saveLesson() {
      if (this.isOffline) {
        window.getApp.sendSnackMessage(
          "Você está offline, não é possível fazer o download",
          "error",
        );
        return;
      }

      if (
        !localStorage.getItem(
          "hasOfflineSupport_" + this.$route.params.sectionItemId,
        )
      ) {
        if (!sessionStorage.getItem("offlineWarn")) {
          sessionStorage.setItem("offlineWarn", true);
          window.getApp.sendSnackMessage(
            "Não feche esta aba até o download terminar",
            "warning",
          );
        }
        this.loadingOfflineSupport = true;
        window.currentlyDonwloading += 1;
        let savedVideo = null;
        try {
          let videoDownload = await CourseService.getVideoLink(
            this.sectionItem.learnable.content_identifier,
          );
          savedVideo = await CourseService.getResolutionDownload(
            videoDownload,
            this.isMobile(),
          );
        } catch (error) {
          this.loadingOfflineSupport = false;
          window.currentlyDonwloading -= 1;
          window.getApp.sendSnackMessage(
            "Não é possível fazer o download deste vídeo",
            "error",
          );
          return;
        }
        let classUrl = `/courses/${this.$route.params.courseId}/classroom/${this.sectionItem.id}/${this.sectionItem.learnable.slug}`;
        try {
          let resources = [savedVideo, classUrl];
          const cacheResources = await swService.cacheResources(resources);
          if (cacheResources) {
            localStorage.setItem(
              "hasOfflineSupport_" + this.$route.params.sectionItemId,
              1,
            );
            this.hasOfflineSupport = true;
            this.loadingOfflineSupport = false;
            window.currentlyDonwloading -= 1;
            window.getApp.sendSnackMessage(
              "Download de aula finalizado",
              "success",
            );
          } else {
            this.loadingOfflineSupport = false;
            window.currentlyDonwloading -= 1;
            window.getApp.sendSnackMessage(
              "Não foi possível concluir o dowload, tente novamente mais tarde.",
              "warning",
            );
          }
        } catch (error) {
          this.loadingOfflineSupport = false;
          window.currentlyDonwloading -= 1;
          window.getApp.sendSnackMessage("Erro ao fazer o download.", "error");
        }
      }
    },

    loadLesson() {
      this.loadingLesson = true;
      this.loadingVideo = true;
      CourseService.getSectionItem(this.$route.params.sectionItemId, false)
        .then(async (response) => {
          this.sectionItem = response.data;
          this.hasVideo = !!this.sectionItem?.learnable?.video;

          this.setSubjectsLabel(this.sectionItem.learnable?.subjects.data);
          try {
            let offlineVideo = await CourseService.getVideoLink(
              this.sectionItem.learnable.content_identifier,
            );
            this.videoResolution = CourseService.getResolutionDownload(
              offlineVideo,
              this.isMobile(),
            );
          } catch (error) {
            console.log("resolution error", error);
          }
          if (this.sectionItem.learnable.video) {
            this.videoId = this.sectionItem.learnable.video.identifier;
            this.videoSource = this.sectionItem.learnable.video.source;
            this.setVideos();
          }
          this.checkAlerts();
          this.loading = false;
          this.loadingLesson = false;
          this.hasOfflineSupport = localStorage.getItem(
            "hasOfflineSupport_" + this.$route.params.sectionItemId,
          );
          if (this.hasOfflineSupport || this.videoSource === "vimeo") {
            this.loadingVideo = false;
          }
          this.$forceUpdate();

          // Load podcast audio for this lesson
          this.loadPodcastAudio();
        })
        .finally(() => {
          window.pandascripttag = window.pandascripttag || [];
          window.pandascripttag.push(() => {
            const player = new PandaPlayer("panda-XXXX", {
              onReady: () => {
                player.onEvent(({ message }) => {
                  if (message === "panda_ended") {
                    this.onPlayerEnded({ detail: null });
                  }
                  if (message === "panda_canplay") {
                    this.loadingVideo = false;
                  }
                });
              },
              onError: (event) => {
                console.log("Player onError", event);
              },
              playerConfigs: {
                autoplay: true,
                playOpensFullscreen: true,
              },
            });
          });
          // this.loadingVideo = false
        })
        .catch((error) => {
          this.loadingLesson = false;

          if (error.isForbiddenError()) {
            window.getApp.sendSnackMessage(
              "No momento, esta aula não está disponível",
              "error",
            );
            this.$router.push({
              name: "course",
              params: {
                courseId: this.$route.params.courseId,
                slug: this.$route.params.slug,
              },
            });
          }
        });
      this.logItem();
    },

    isMobile() {
      return this.$vuetify.breakpoint.xs;
    },
    width750() {
      return this.$vuetify.breakpoint.width < 750;
    },
    width450() {
      return this.$vuetify.breakpoint.width < 415;
    },

    async getMock() {
      this.loadingMock = true;
      const query = {
        limit: 50,
        neverSolved: false,
        mode: 1,
        courseId: this.$route.params.courseId,
        topics: this.sectionItem?.section_topic_id ?? null,
        subjects:
          this.sectionItem?.learnable?.subjects?.data?.length > 0
            ? this.sectionItem.learnable?.subjects?.data?.[0]?.id
            : null,
      };

      if (query.subjects === null) {
        delete query.subjects;
        query.topics = [
          this.sectionItem?.section_topic_id,
          this.sectionItem?.learnable?.topic_id,
        ];
      }

      if (Array.isArray(query.topics)) {
        query.topics = query.topics
          .filter((topic) => topic !== null && topic !== undefined)
          .filter((topic, index, self) => self.indexOf(topic) === index);
        if (query.topics.length === 0) {
          delete query.topics;
        }
      }
      const course = (
        await CourseService.getCourseById(this.$route.params.courseId)
      )?.data;

      this.$gtag.query("event", "quiz_by_subjects", {
        course: course?.course,
        lesson: this.sectionItem?.learnable?.lesson,
        topics: this.sectionItem?.learnable?.topic_id ?? null,
        subjects:
          this.sectionItem?.learnable?.subjects?.data?.length > 0
            ? this.sectionItem?.learnable?.subjects?.data?.[0]?.subject
            : null,
      });
      this.loadingMock = false;
      this.$router.push({
        name: "mocks.start",
        params: { id: "new" },
        query: query,
      });
    },

    loadRating(sectionItemId = null) {
      if (sectionItemId == null)
        sectionItemId = this.$route.params.sectionItemId;
      RatingService.getRatingByItemId(sectionItemId, true)
        .then((response) => {
          this.rating = response.data;
        })
        .catch((error) => {
          console.log("error", error);
        });
    },

    setVideos() {
      const video = this.sectionItem.learnable.video;
      if (video == null) return;
      const srcs = [];
      if (video.media_hls_url) {
        srcs.push({
          src: video.media_hls_url,
          type: "video/mp4",
          size: 720,
        });
      }
      this.source = {
        type: "video",
        title: video.video,
        poster: video.big_thumbnail,
        sources: srcs,
      };
    },

    setSubjectsLabel(collection) {
      const _this = this;
      _this.subjectsLabel = "";
      const subjectsList = collection;
      for (let i = 0; i < subjectsList.length; i++) {
        if (_this.subjectsLabel.length > 0) _this.subjectsLabel += ", ";
        _this.subjectsLabel += subjectsList[i].subject;
      }
    },

    classChanged() {
      this.$data.source = {};
      this.hasOfflineSupport =
        localStorage.getItem(
          "hasOfflineSupport_" + this.$route.params.sectionItemId,
        ) == "1";
      this.podcastAudioSrc = null;
      this.loadLesson();
      this.loadRating();
    },
    vuePlyrOnError() {
      if (this.isOffline) {
        this.plyrOffline.on("error", async (errorPlyr) => {
          console.error("plyrError: ", errorPlyr);
          const dd = deviceDetector.parse(navigator.userAgent);
          const device = `${dd.client.name} ${dd.client.version} ${dd.os.name} ${dd.os.version} ${dd.os.platform}`;
          const lesson = this.sectionItem.learnable.lesson;
          const videoLink = this.videoResolution;
          const isVideoOnCache = await this.isVideoLinkCached(videoLink);
          const logErroPlyr = {
            device,
            lesson,
            videoLink,
            isVideoOnCache,
            errorPlyr,
          };
          localStorage.setItem(
            "plyrError_" + this.sectionItem.id,
            JSON.stringify(logErroPlyr),
          );
        });
      }
    },
    vuePlyrStalled() {
      if (this.isOffline) {
        this.plyrOffline.on("stalled", async (event) => {
          console.warn("stalled:", event);
          const dd = deviceDetector.parse(navigator.userAgent);
          const device = `${dd.client.name} ${dd.client.version} ${dd.os.name} ${dd.os.version} ${dd.os.platform}`;
          const lesson = this.sectionItem.learnable.lesson;
          const videoLink = this.videoResolution;
          const isVideoOnCache = await this.isVideoLinkCached(videoLink);
          const logStalledPlyr = {
            device,
            lesson,
            videoLink,
            isVideoOnCache,
            event,
          };
          localStorage.setItem(
            "plyrStalled_" + this.sectionItem.id,
            JSON.stringify(logStalledPlyr),
          );
        });
      }
    },
    async isVideoLinkCached(videoLink) {
      try {
        const cache = await caches.open(CONFIG.CACHEV);
        const linkIsOnCache = await cache.match(videoLink);
        if (linkIsOnCache) {
          return true;
        } else {
          console.log("Link not found on cache");
          return false;
        }
      } catch (error) {
        console.error("error searching on cache:", error);
        return false;
      }
    },

    // listen event
    onPlayerTimeupdate({ detail }) {},
    onCanplay() {
      this.player.play();
    },
    onPlayerEnded({ detail }) {
      // Random to define if move next or ask for rating
      const number = Math.round(Math.random() * 4);
      if (number === 0) this.showRatingDialog = true;
      else this.moveLesson("next");
    },
    moveLesson(direction) {
      if (this.isOffline) {
        // sections q tem algo de offline
        const sections_com_item_off = this.sections.filter(
          (section) =>
            section.items.data.filter((item) =>
              this.offline_section_items.find(
                (offline_item) => offline_item.id == item.id,
              ),
            ).length,
        );
        const items = sections_com_item_off.map((e) => e.items.data).flat();
        const items_off = items.filter((item) =>
          this.offline_section_items.find(
            (offline_item) => offline_item.id == item.id,
          ),
        );
        switch (direction) {
          case "next":
            const next =
              items_off[
                items_off.findIndex(
                  (x) => x.id == this.$route.params.sectionItemId,
                ) + 1
              ];

            if (next) {
              this.sectionItem = next;
              this.$router.push({
                name: "classroom",
                params: {
                  courseId: this.$route.params.courseId,
                  sectionItemId: this.sectionItem.id,
                  slug: this.sectionItem.learnable.slug,
                },
              });
              this.loadLesson();
              this.loadRating(this.sectionItem.id);
              this.loadPodcastAudio();
              this.loadingLesson = false;
            } else {
              this.$router.push({
                name: "course",
                params: {
                  courseId: this.$route.params.courseId,
                  slug: this.$route.params.slug,
                },
              });
            }
            break;
          case "previous":
            const previous =
              items_off[
                items_off.findIndex(
                  (x) => x.id == this.$route.params.sectionItemId,
                ) - 1
              ];
            if (previous) {
              this.sectionItem = previous;
              this.$router.push({
                name: "classroom",
                params: {
                  courseId: this.$route.params.courseId,
                  sectionItemId: this.sectionItem.id,
                  slug: this.sectionItem.learnable.slug,
                },
              });
              this.loadLesson();
              this.loadRating(this.sectionItem.id);
              this.loadPodcastAudio();
              this.loadingLesson = false;
            } else {
              this.$router.push({
                name: "course",
                params: {
                  courseId: this.$route.params.courseId,
                  slug: this.$route.params.slug,
                },
              });
            }

            break;
        }
        return;
      }

      this.$data.source = {};
      this.loadingLesson = true;
      this.loadingVideo = true;
      CourseService.getMovedSectionItem(
        this.$route.params.courseId,
        this.sectionItem.id,
        direction,
      )
        .then((response) => {
          this.sectionItem = response.data;
          this.$router.push({
            name: "classroom",
            params: {
              courseId: this.$route.params.courseId,
              sectionItemId: this.sectionItem.id,
              slug: this.sectionItem.learnable.slug,
            },
          });
          this.loadLesson();
          this.loadRating(this.sectionItem.id);
          this.loadPodcastAudio();
          this.loadingLesson = false;
        })
        .catch((error) => {
          if (error.status === 404) {
            window.getApp.sendSnackMessage(
              "No momento, não temos mais aulas disponíveis",
              "error",
            );

            this.$router.push({
              name: "course",
              params: {
                courseId: this.$route.params.courseId,
                slug: this.$route.params.slug,
              },
            });
          }
          this.loadingLesson = false;
        });
    },

    logItem() {
      CourseService.logSectionItem(this.$route.params.sectionItemId).then(
        (response) => {},
      );
    },
    checkAlerts() {
      SessionAlertService.checkPendingAlerts().then((response) => {});
    },
    openRatingDialog() {
      this.showRatingDialog = true;
      this.player
        .getPictureInPicture()
        .then(function (pip) {
          // pip = whether picture-in-picture is turned on or off
        })
        .catch(function (error) {
          // an error occurred
        });
    },
    closeRatingDialog() {
      this.showRatingDialog = false;
    },
    onEnterpictureinpicture() {},
    pauseVideo() {
      if (
        this.hasOfflineSupport &&
        this.$refs.plyrOffline &&
        this.$refs.plyrOffline.player
      ) {
        this.$refs.plyrOffline.player.pause();
      } else if (
        this.videoSource === "vimeo" &&
        this.$refs.vp &&
        this.$refs.vp.$refs.plyr
      ) {
        this.$refs.vp.$refs.plyr.player.pause();
      } else if (this.videoSource === "panda") {
        const iframe = document.getElementById("panda-XXXX");
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage({ type: "pause" }, "*");
        }
      }
    },
    pausePodcast() {
      if (this.$refs.podcastPlayer) {
        this.$refs.podcastPlayer.pause();
      }
    },
    onVideoPlay() {
      if (this.$refs.podcastPlayer) {
        this.$refs.podcastPlayer.pause();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.video-player-div {
  max-height: 60vh;
}
// essa abordagem pode parecer estranha mas é a mais comum para videos responsivos.
// https://css-tricks.com/fluid-width-video/
.iframe-wrapper {
  position: relative;
  padding-bottom: 0%;
  height: 0;
  &.videoReady {
    padding-bottom: 56.25%;
  }
  .iframe {
    position: absolute;
    border: 0;
    border-radius: 2vw;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    max-height: 60vh;
    padding: 20px;
  }
  @media screen and (max-width: 600px) {
    .iframe {
      padding: 0px;
      border-radius: 0;
    }
  }
}
.video-actions {
  display: flex;
  justify-content: space-between;
  padding: 0 19px;
  max-width: 1200px;
  margin: 0 auto;
  @media (max-width: 600px) {
    padding: 18px 3px 0;
  }
}
.menu-btn-mobile {
  @media (max-width: 750px) {
    display: flex;
  }
  @media (min-width: 751px) {
    display: none;
  }
}
.class-title {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-left: 0;
  text-align: center;
  margin: 0 auto;
}
@media (max-width: 600px) {
  .class-title {
    padding-left: 0;
  }
}
::v-deep .v-btn__content {
  @media (max-width: 450px) {
    display: flex;
    flex-direction: column !important;
  }
}
@keyframes skeleton-blink {
  0% {
    background-color: #e0e0e0;
  }
  50% {
    background-color: #f5f5f5;
  }
  100% {
    background-color: #e0e0e0;
  }
}
.filler-div {
  position: relative;
  padding-bottom: 54vh;
  height: 0;
  background-color: grey;
  border-radius: 10px;
  margin: 20px;
  animation: skeleton-blink 1.5s infinite ease-in-out;
  @media (max-width: 550px) {
    padding-bottom: 24vh;
    margin: 0;
    margin-bottom: 15px;
  }
}
.flex-column {
  flex-direction: column !important;
}

.d-flex {
  display: flex !important;
}

.video {
  height: calc(100vh - 250px);
}
</style>
